import React, { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../store/indexRedux';
import { autoSelectCompany } from '../utils/autoSelectCompany';
import Loading from '../components/ui/Loading';
import { fetchStatementPermalink, PermalinkError, StatementPermalinkResponse } from '../api/permalinks';

type LoadStatus = 'loading' | 'ok' | 'notfound' | 'unauthorized' | 'error';

const StatementPermalinkPage: React.FC = () => {
  const { t } = useTranslation('permalinks');
  const { documentId } = useParams<{ documentId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const selectedUserCompany = useSelector((s: RootState) => s.selectedCompany.selectedUserCompany);

  const [data, setData] = useState<StatementPermalinkResponse | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');

  useEffect(() => {
    if (!documentId) {
      setStatus('notfound');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    fetchStatementPermalink(documentId)
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
        setStatus('ok');
        window.scrollTo({ top: 0, behavior: 'smooth' });

        autoSelectCompany(dispatch, resp.company.id, selectedUserCompany?.company_id);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof PermalinkError && err.status === 401) setStatus('unauthorized');
        else if (err instanceof PermalinkError && err.status === 404) setStatus('notfound');
        else setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  if (status === 'loading') return <Loading />;

  if (status !== 'ok' || !data) {
    const message =
      status === 'unauthorized' ? t('sessionExpired') : status === 'error' ? t('errorGeneric') : t('statementNotFound');
    return (
      <div className='flex flex-col items-center mt-32 px-4'>
        <div className='lg:w-[560px] w-full p-6 border border-primary-100 dark:border-primary-800 rounded-lg bg-white dark:bg-primary-900 text-center'>
          <p className='text-primary-600 dark:text-primary-100 mb-6'>{message}</p>
          {status === 'unauthorized' ? (
            <NavLink
              to='/'
              className='inline-block px-5 py-2.5 bg-primary-600 text-white text-sm rounded-3xl hover:bg-primary-700 font-semibold'
            >
              {t('logInAgain')}
            </NavLink>
          ) : (
            <NavLink
              to='/documents'
              className='inline-block px-5 py-2.5 bg-primary-600 text-white text-sm rounded-3xl hover:bg-primary-700 font-semibold'
            >
              {t('backToDocuments')}
            </NavLink>
          )}
        </div>
      </div>
    );
  }

  const fileName = data.document.file_name;
  const isImage = /\.(png|jpe?g)$/i.test(fileName);

  return (
    <div className='md:mx-16 pb-[80px] mt-20 mb-16'>
      <h2 className='text-primary-600 dark:text-white lg:text-[1.875rem] font-semibold mb-2'>{t('statementTitle')}</h2>
      <p className='text-primary-400 text-sm font-semibold'>{fileName}</p>
      <p className='text-primary-400 text-xs mb-8'>
        {data.company.name}
        {data.company.kbo ? ` · ${t('kbo')} ${data.company.kbo}` : ''}
      </p>

      <div className='mt-8 mb-8'>
        <NavLink
          to='/documents'
          className='px-[1.25rem] py-[0.53rem] bg-primary-600 dark:bg-primary-600 text-sm text-white rounded-3xl hover:bg-primary-700 dark:hover:bg-primary-700 font-semibold'
        >
          {t('backToDocuments')}
        </NavLink>
      </div>

      {isImage ? (
        <img src={data.url} alt={t('statementTitle')} className='w-full h-auto' />
      ) : (
        <iframe src={data.url} width='100%' height='700px' title={t('statementTitle')}></iframe>
      )}
    </div>
  );
};

export default StatementPermalinkPage;
